import {ChangeDetectionStrategy, Component, computed, input, model} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {DirectusTranslatedItem, DirectusTranslatedItemGroup} from '@frontend/directus';
import {ModelFieldMultiselectComponent} from '../../../inputs/model-field/model-field-multiselect/model-field-multiselect.component';
import {ModelFieldNumberComponent} from '../../../inputs/model-field/model-field-number/model-field-number.component';
import {GlobalSearchComponent} from '../../../search/global-search/global-search.component';
import {ProjectReference} from '../project-references.types';
import {toUsedOptions} from './project-references-filter.utils';
import {ProjectReferenceFilter} from './project-references-filter.types';

@Component({
  selector: 'app-project-references-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    GlobalSearchComponent,
    ModelFieldMultiselectComponent,
    ModelFieldNumberComponent,
    TranslocoPipe,
  ],
  templateUrl: './project-references-filter.component.html',
})
export class ProjectReferencesFilterComponent {
  readonly references = input.required<ProjectReference[]>();
  readonly industryOptions = input.required<DirectusTranslatedItem[]>();
  readonly moduleGroups = input.required<DirectusTranslatedItemGroup[]>();
  readonly countryOptions = input.required<DirectusTranslatedItem[]>();
  readonly languageOptions = input.required<DirectusTranslatedItem[]>();

  readonly filter = model.required<ProjectReferenceFilter>();

  protected readonly usedIndustryOptions = computed<DirectusTranslatedItem[]>(() => {
    const usedIds: string[] = this.references().flatMap((current) =>
      current.industryId === null ? [] : [current.industryId],
    );

    return toUsedOptions(this.industryOptions(), usedIds);
  });

  protected readonly usedFunctionOptions = computed<DirectusTranslatedItem[]>(() => {
    const functionOptions: DirectusTranslatedItem[] = this.moduleGroups().flatMap(
      (current) => current.items,
    );
    const usedIds: string[] = this.references()
      .flatMap((current) => current.functionalScope)
      .map((current) => current.functionId);

    return toUsedOptions(functionOptions, usedIds);
  });

  protected readonly usedCountryOptions = computed<DirectusTranslatedItem[]>(() => {
    const usedIds: string[] = this.references().flatMap((current) => current.countryCodes);

    return toUsedOptions(this.countryOptions(), usedIds);
  });

  protected readonly usedLanguageOptions = computed<DirectusTranslatedItem[]>(() => {
    const usedIds: string[] = this.references().flatMap((current) => current.languageCodes);

    return toUsedOptions(this.languageOptions(), usedIds);
  });

  protected updateFilter<K extends keyof ProjectReferenceFilter>(
    key: K,
    value: ProjectReferenceFilter[K],
  ): void {
    const updated: ProjectReferenceFilter = {...this.filter(), [key]: value};

    this.filter.set(updated);
  }

  protected onMinHeadcountChange(minHeadcount: number | null): void {
    this.updateFilter('minHeadcount', minHeadcount ?? 0);
  }
}
